import { Suspense, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../common/store";
import { Popup } from "../../components/pop-up";
import { themeSelector } from "../../../common/store/theme/selectors";
import { BookAppointmentImplementation } from "../../../common/store/theme/constants/book-appointment-implementation";
import { BookAppointmentSkeleton } from "./components/book-appointment-skeleton";
import {
  closeBookAppointment,
  setBookAppointment,
} from "../../../common/store/theme/theme-slice";
import { useHubspotFormSubmission } from "../../hooks/use-hubspot-form-submission";
import CalendlyWidget from "./components/calendly";
import CallbackForm from "./components/callback";
import Hubspot from "./components/hubspot";
import StrategyContainer from "./components/strategy-container";
import getBasicLSData from "../../utils/getBasicLSData";
import { axiosClient } from "../../../common/api/axios-client";
import { HttpStatusCode } from "axios";
// import { CustomCalendar } from "./components/custom-calendar";
import CustomCalendarScheduler from "./components/custom-calendar/CustomCalendarScheduler";

import { chatSelector } from "../../../common/store/chat/selectors";
import { fetchMeetingDetails } from "../../utils/fetch-meeting-details";
import { setAgentDetails, setAvailableSlots, setMeetingDetails } from "../../../common/store/chat/chat-slice";

export const BookAppointment = () => {
  const storeTheme = useAppSelector(themeSelector);
  const storeChat = useAppSelector(chatSelector);
  const merchIdHash = useAppSelector((state) => state["app-state"].merchIdHash);
  const visitorCountryCodeSource = useAppSelector((state) => state["app-state"].visitorCountryCodeSource)
  const visitorCountryCodeKey = useAppSelector((state) => state["app-state"].visitorCountryCodeKey)
  const visitorCountrySource = useAppSelector((state) => state["app-state"].visitorCountrySource)
  const visitorCountryKey = useAppSelector((state) => state["app-state"].visitorCountryKey)
  const dispatch = useAppDispatch();
  const meetingDetails = storeChat.meetingDetails;
  const agentDetails = storeChat.agentDetails;
  const customCalendarSlots = storeChat.availaleSlots;
  const onClose = () => {
    dispatch(closeBookAppointment());
  };
  const shouldUseCustomCalendar = useAppSelector(
    (state) => state.chat.customCalendar
  );
  const strategies = useMemo(() => {
    const result: Record<string, JSX.Element> = {
      [BookAppointmentImplementation.CALLBACK]: <CallbackForm />,
    };

    if (
      merchIdHash ===
      "b6783969bc077c42bef036b471e89631d7e6ca9d4f1a6032b5ad2c0e982a7dec"
    ) {
      result[BookAppointmentImplementation.HUBSPOT] = <Hubspot />;
    }

    else if (shouldUseCustomCalendar) {
      result[BookAppointmentImplementation.CUSTOM_CALENDAR] = <CustomCalendarScheduler agentDetails={agentDetails} availableSlots={customCalendarSlots || {}} meetingDetails={meetingDetails} onClose = {onClose}/>
    } 
    else {
      result[BookAppointmentImplementation.CALENDLY] = (
        <CalendlyWidget onClose={onClose} />
      );
    }

    return result;
  }, [merchIdHash , customCalendarSlots,meetingDetails, agentDetails , storeTheme]);

  const calendlyUrl =
    useAppSelector((state) => state.theme.themeConfig.calendlyLink) || "";

  useHubspotFormSubmission();

  const handleMeetingSchedule = async () => {

    const request_obj = {
      ...getBasicLSData(visitorCountryCodeSource, visitorCountryCodeKey, visitorCountrySource, visitorCountryKey),
      frontend_source: "schedule_a_meeting",
    };

    if (
      merchIdHash !==
      "b6783969bc077c42bef036b471e89631d7e6ca9d4f1a6032b5ad2c0e982a7dec"
    ) {
      try {
        const response = await axiosClient.post(
          "/assign_sales_agent",
          request_obj
        );

        if (response.status !== HttpStatusCode.Ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = response.data;

        dispatch(
          setBookAppointment({
            implementation: BookAppointmentImplementation.CALENDLY,
            calendlyLink: data?.calendar_link,
          })
        );
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };
  const handleCustomCalendar = async () => {

    const request_obj = {
      ...getBasicLSData(visitorCountryCodeSource, visitorCountryCodeKey, visitorCountrySource, visitorCountryKey),
      frontend_source: "schedule_a_meeting",
    };
        
    if (
      merchIdHash !==
      "b6783969bc077c42bef036b471e89631d7e6ca9d4f1a6032b5ad2c0e982a7dec"
    ) {
      try {
        const response = await axiosClient.post(
          "/calendar/assign_agent",
          request_obj
        );

        if (response.status !== HttpStatusCode.Ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = response.data;

        dispatch(
          setBookAppointment({
            implementation: BookAppointmentImplementation.CUSTOM_CALENDAR,
            agentEmail : data?.agent_email,
          })
        );
        const slots = await fetchMeetingDetails(data.agent_email || "");
        dispatch(setMeetingDetails(slots.meeting_details));
        dispatch(setAgentDetails(slots.agent_details));
        dispatch(setAvailableSlots(slots.available_slots));
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };

  useEffect(() => {
    const clickEventFunction = (e: any) => {
      if (e.target.id === "suggestion_btn") {
        let type = ''
        if (shouldUseCustomCalendar) {
          type = BookAppointmentImplementation.CUSTOM_CALENDAR;
        }
        else{
          type = e.target.getAttribute(
            "data-type"
          ) as BookAppointmentImplementation;
        }
        if (e.detail === 1) {
          switch (type) {
            case BookAppointmentImplementation.CALLBACK:
              dispatch(
                setBookAppointment({
                  implementation: BookAppointmentImplementation.CALLBACK,
                })
              );
              break;
            case BookAppointmentImplementation.CALENDLY:
              handleMeetingSchedule();
              // dispatch(setBookAppointment({ implementation: BookAppointmentImplementation.CALENDLY }));
              break;
            case BookAppointmentImplementation.CUSTOM_CALENDAR:
              handleCustomCalendar();              
              break;
          }
        }
      }
    };
    
    document.addEventListener("click", clickEventFunction);
    return () => {
      document.removeEventListener("click", clickEventFunction);
    };
  }, [calendlyUrl,agentDetails,meetingDetails]);

  return (
    <Popup
      isOpen={storeTheme.themeConfig.isOpenBookAppointment}
      onClose={onClose}
    >
      <Suspense fallback={<BookAppointmentSkeleton />}>
        {(Object.keys(strategies) as BookAppointmentImplementation[]).map(
          (key) => (
            <StrategyContainer
              key={key}
              visible={
                key === storeTheme.themeConfig.bookAppointmentImplementation
              }
            >
              {strategies[key]}
            </StrategyContainer>
          )
        )}
      </Suspense>
    </Popup>
  );
};
